import { PropsWithChildren, useLayoutEffect, useRef, useState } from 'react'

import { AnimatePresence, motion, Variants } from 'framer-motion'

import { MoreIcon } from '@/src/assets/icons'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

import styles from './MoreButton.module.css'

interface IMoreButtonProps extends PropsWithChildren {
  isOpened: boolean
  waitingParticipants?: number

  togglePopover: () => void
}

const popoverVariants: Variants = {
  hidden: {
    opacity: 0,
    x: '-50%',
    y: 20,
  },
  visible: (custom: { translateX: string }) => ({
    opacity: 1,
    y: 0,
    x: custom.translateX ?? '-50%',
    scale: 1,
    transition: {
      type: 'spring',
      damping: 20,
      stiffness: 300,
    },
  }),
  exit: {
    opacity: 0,
    x: '-50%',
    y: 10,
    transition: { duration: 0.15 },
  },
}

const buttonVariants: Variants = {
  tap: { scale: 0.9 },
}

export const MoreButton = ({
  children,
  isOpened,
  waitingParticipants = 0,
  togglePopover,
}: IMoreButtonProps) => {
  const { text_primary_color, primary_color } = useAppSelector(selectBranding)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const [position, setPosition] = useState({ translateX: '-50%' })

  useLayoutEffect(() => {
    const updatePopoverPosition = () => {
      if (buttonRef.current && popoverRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect()
        const popoverRect = popoverRef.current.getBoundingClientRect()

        const buttonCenterX = buttonRect.left + buttonRect.width / 2

        const popoverRightEdge = buttonCenterX + popoverRect.width / 2
        const windowWidth = window.innerWidth
        const safeMargin = 10

        let translateX = '-50%'

        if (popoverRightEdge > windowWidth - safeMargin) {
          const overflow = popoverRightEdge - (windowWidth - safeMargin)
          translateX = `calc(-50% - ${overflow}px)`
        }

        const popoverLeftEdge = buttonCenterX - popoverRect.width / 2
        if (popoverLeftEdge < safeMargin) {
          const overflow = safeMargin - popoverLeftEdge
          translateX = `calc(-50% + ${overflow}px)`
        }

        setPosition({ translateX })
      }
    }

    if (isOpened) {
      requestAnimationFrame(() => {
        updatePopoverPosition()
      })
    }

    const handleResize = () => {
      if (isOpened) {
        updatePopoverPosition()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpened])

  return (
    <div className={styles?.container}>
      {!isOpened && waitingParticipants > 0 && (
        <span className="lk-counter" style={{ background: primary_color }}>
          {waitingParticipants < 10 ? waitingParticipants : '9+'}
        </span>
      )}

      <AnimatePresence>
        {isOpened && (
          <motion.div
            ref={popoverRef}
            className={styles?.popover}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={popoverVariants}
            custom={position}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        ref={buttonRef}
        className={styles?.button}
        onClick={togglePopover}
        variants={buttonVariants}
        whileTap="tap"
      >
        <MoreIcon fill={text_primary_color ?? '#fff'} />
      </motion.button>
    </div>
  )
}
